import type { SupabaseClient } from "@supabase/supabase-js";

export interface FinanceWarehouseReport {
  finance: {
    postedSalesToman: number;
    postedPurchasesToman: number;
    postedExpensesToman: number;
    postedTransactionToman: number;
    recordedPaymentsToman: number;
    postedPaymentsToman: number;
    unpaidBalanceToman: number;
    postedCount: number;
    voidedCount: number;
  };
  assets: {
    assetCount: number;
    activeAssetCount: number;
    purchaseValueToman: number;
    quantityTotal: number;
  };
  inventory: {
    available: boolean;
    itemCount?: number;
    activeItemCount?: number;
    lowStockCount?: number;
    movementCount?: number;
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function numberValue(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return 0;
}

export function parseFinanceWarehouseReport(value: unknown): FinanceWarehouseReport {
  const root = record(value);
  const finance = record(root.finance);
  const assets = record(root.assets);
  const inventory = record(root.inventory);
  const inventoryAvailable = inventory.available === true;

  return {
    finance: {
      postedSalesToman: numberValue(finance.postedSalesToman),
      postedPurchasesToman: numberValue(finance.postedPurchasesToman),
      postedExpensesToman: numberValue(finance.postedExpensesToman),
      postedTransactionToman: numberValue(finance.postedTransactionToman),
      recordedPaymentsToman: numberValue(finance.recordedPaymentsToman),
      postedPaymentsToman: numberValue(finance.postedPaymentsToman),
      unpaidBalanceToman: numberValue(finance.unpaidBalanceToman),
      postedCount: numberValue(finance.postedCount),
      voidedCount: numberValue(finance.voidedCount),
    },
    assets: {
      assetCount: numberValue(assets.assetCount),
      activeAssetCount: numberValue(assets.activeAssetCount),
      purchaseValueToman: numberValue(assets.purchaseValueToman),
      quantityTotal: numberValue(assets.quantityTotal),
    },
    inventory: {
      available: inventoryAvailable,
      ...(inventoryAvailable
        ? {
            itemCount: numberValue(inventory.itemCount),
            activeItemCount: numberValue(inventory.activeItemCount),
            lowStockCount: numberValue(inventory.lowStockCount),
            movementCount: numberValue(inventory.movementCount),
          }
        : {}),
    },
  };
}

export async function loadFinanceWarehouseReport(
  client: SupabaseClient,
  farmId: string,
): Promise<FinanceWarehouseReport> {
  const result = await client.rpc("finance_warehouse_report_v1", {
    target_farm_id: farmId,
  });

  if (result.error) throw result.error;
  return parseFinanceWarehouseReport(result.data);
}
